import { inject, Injectable } from '@angular/core';
import { from, Observable, of, ReplaySubject, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { environment } from 'environments/environment';
import type { Navigation } from 'app/core/navigation/navigation.types';
import { normalizeAdminPath } from '../guards/admin-permission.utils';
import type { ApiResponse } from '../models';
import { ApiService } from '../services/api.service';
import { UserService } from '../services/user.service';

interface PermissionMenuItem {
  FunctionID: string;
  Name: string;
  IconMenu: string;
  URL: string;
}

@Injectable({
  providedIn: 'root'
})
export class NavigationService {

  private readonly _apiService = inject(ApiService);
  private readonly _toastr = inject(ToastrService);
  private readonly _userService = inject(UserService);

  private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);
  private _currentNavigation: Navigation | null = null;
  private _loadedEmployeeCode: string | null = null;

  get navigation$(): Observable<Navigation> {
    return this._navigation.asObservable();
  }

  get(): Observable<Navigation> {
    const user = this._userService.userInfo;

    if (!user) {
      return of(this._buildAndPublishEmptyNavigation());
    }

    if (this._currentNavigation && this._loadedEmployeeCode === user.employeeCode) {
      return of(this._currentNavigation);
    }

    return from(this._load(user.employeeCode)).pipe(
      tap((navigation) => this._publishNavigation(navigation, user.employeeCode))
    );
  }

  /**
   * Never rejects: a guard and a resolver both wait on this, and a thrown error there cancels
   * the navigation outright — leaving the user on a blank page with nothing explaining why.
   * A failed load is an empty menu plus a toast instead.
   */
  private async _load(employeeCode: string): Promise<Navigation> {
    const response = await this._apiService.post<ApiResponse<PermissionMenuItem[]>>(
      '/Permission/GetUserPermission',
      { systemId: environment.systemId, employeeCode }
    );

    if (!response.success) {
      this._toastr.error(response.message || 'Could not load the menu', 'Notification');
      return this._createEmptyNavigation();
    }

    return this._buildNavigation(response.data ?? []);
  }

  private _buildAndPublishEmptyNavigation(): Navigation {
    const emptyNavigation = this._createEmptyNavigation();

    this._publishNavigation(emptyNavigation, null);

    return emptyNavigation;
  }

  private _buildNavigation(items: PermissionMenuItem[]): Navigation {
    const menu: FuseNavigationItem[] = items.map((item) => ({
      title: item.Name,
      type: 'basic',
      icon: item.IconMenu,
      link: normalizeAdminPath(item.URL)
    }));

    return {
      compact: menu,
      default: menu,
      futuristic: menu,
      horizontal: menu
    };
  }

  private _publishNavigation(navigation: Navigation, employeeCode: string | null): void {
    this._currentNavigation = navigation;
    this._loadedEmployeeCode = employeeCode;
    this._navigation.next(navigation);
  }

  private _createEmptyNavigation(): Navigation {
    return {
      compact: [],
      default: [],
      futuristic: [],
      horizontal: []
    };
  }

}
